/**
 * OAuth 2.1 consent page for the Supabase Auth OAuth server (docs/PRD_MCP_SERVER.md phase 2).
 * GoTrue redirects here as `oauth_server_authorization_path` with ?authorization_id=; we show
 * what's being connected and call supabase.auth.oauth.approve/denyAuthorization, then follow
 * the returned redirect_url back to the client (Claude, ChatGPT, Cursor, ...).
 *
 * Requires an app session: the OAuth token is minted AS this user, and RLS scopes everything
 * the connector can read to exactly what they see in Upstand.
 */
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@saas-boilerplate/shared';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Loader2, ShieldCheck, AlertCircle, Bot } from 'lucide-react';

type Details = {
  client_name: string;
  redirect_host: string | null;
  scope: string | null;
};

function extractDetails(raw: unknown): Details {
  const d = (raw || {}) as Record<string, unknown>;
  const client = (d.client || {}) as Record<string, unknown>;
  const name =
    (typeof client.client_name === 'string' && client.client_name) ||
    (typeof d.client_name === 'string' && d.client_name) ||
    (typeof client.name === 'string' && client.name) ||
    'An AI client';
  const redirectUri =
    (typeof d.redirect_uri === 'string' && d.redirect_uri) ||
    (typeof client.redirect_uri === 'string' && client.redirect_uri) ||
    '';
  let host: string | null = null;
  try {
    host = redirectUri ? new URL(redirectUri).host : null;
  } catch {
    host = null;
  }
  return { client_name: String(name), redirect_host: host, scope: typeof d.scope === 'string' ? d.scope : null };
}

export function OAuthConsentPage() {
  const params = new URLSearchParams(window.location.search);
  const authorizationId = params.get('authorization_id') || '';

  const [phase, setPhase] = useState<'loading' | 'signin' | 'ready' | 'working' | 'done' | 'error'>('loading');
  const [details, setDetails] = useState<Details | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!authorizationId) {
      setError('This link is missing its authorization id.');
      setPhase('error');
      return;
    }
    setPhase('loading');
    const { data: sessionData } = await supabase.auth.getSession();
    const session = sessionData?.session;
    if (!session) {
      setPhase('signin');
      return;
    }
    setUserEmail(session.user?.email || null);
    try {
      const { data, error: dErr } = await (supabase.auth as any).oauth.getAuthorizationDetails(authorizationId);
      if (dErr) throw dErr;
      setDetails(extractDetails(data));
      setPhase('ready');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load this authorization request.');
      setPhase('error');
    }
  }, [authorizationId]);

  useEffect(() => {
    void load();
  }, [load]);

  const decide = async (approve: boolean) => {
    setPhase('working');
    try {
      if (approve) {
        // Allow IS the personal opt-in (double opt-in with the workspace admin toggle). The mcp
        // function checks this flag on every call; Security & Privacy settings is the off-switch.
        const { data: sessionData } = await supabase.auth.getSession();
        const uid = sessionData?.session?.user?.id;
        if (uid) {
          const { error: flagErr } = await supabase
            .from('profiles')
            .update({ ai_connectors_enabled: true })
            .eq('id', uid);
          if (flagErr) throw flagErr;
        }
      }
      const api = (supabase.auth as any).oauth;
      const { data, error: aErr } = approve
        ? await api.approveAuthorization(authorizationId)
        : await api.denyAuthorization(authorizationId);
      if (aErr) throw aErr;
      const redirect = (data as { redirect_url?: string } | null)?.redirect_url;
      setPhase('done');
      if (redirect) window.location.assign(redirect);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not complete the request.');
      setPhase('error');
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4 py-12">
      <Card className="w-full max-w-md">
        {phase === 'loading' && (
          <CardContent className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </CardContent>
        )}

        {phase === 'signin' && (
          <>
            <CardHeader>
              <div className="mb-2 flex items-center gap-2 text-primary">
                <ShieldCheck className="h-5 w-5" />
                <CardTitle>Sign in to continue</CardTitle>
              </div>
              <CardDescription>
                An AI client is asking to connect to your Upstand account. Sign in first, then come back
                here to review the request.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button asChild className="w-full">
                <a href="/" target="_blank" rel="noreferrer">
                  Open Upstand and sign in
                </a>
              </Button>
              <Button variant="outline" className="w-full" onClick={() => void load()}>
                I have signed in, continue
              </Button>
            </CardContent>
          </>
        )}

        {(phase === 'ready' || phase === 'working') && (
          <>
            <CardHeader>
              <div className="mb-2 flex items-center gap-2 text-primary">
                <Bot className="h-5 w-5" />
                <CardTitle>Connect {details?.client_name || 'an AI client'}?</CardTitle>
              </div>
              <CardDescription>
                {details?.client_name || 'This client'} will connect over MCP and can read the meetings,
                transcripts, decisions and action items you can see in Upstand, in workspaces where an
                admin has enabled MCP. It cannot change or delete anything.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="rounded-lg bg-muted/40 p-3 text-xs text-muted-foreground">
                <p>Connecting as {userEmail || 'your account'}.</p>
                {details?.redirect_host ? <p className="mt-1">After approval you return to {details.redirect_host}.</p> : null}
              </div>
              <div className="flex gap-2">
                <Button className="flex-1" disabled={phase === 'working'} onClick={() => void decide(true)}>
                  {phase === 'working' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Allow
                </Button>
                <Button variant="outline" className="flex-1" disabled={phase === 'working'} onClick={() => void decide(false)}>
                  Deny
                </Button>
              </div>
            </CardContent>
          </>
        )}

        {phase === 'done' && (
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            Taking you back to the app...
          </CardContent>
        )}

        {phase === 'error' && (
          <>
            <CardHeader>
              <div className="mb-2 flex items-center gap-2 text-rose-600">
                <AlertCircle className="h-5 w-5" />
                <CardTitle>Something went wrong</CardTitle>
              </div>
              <CardDescription>{error}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild variant="outline" className="w-full">
                <a href="/">Open Upstand</a>
              </Button>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
}
